import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useRecoilValue, useSetRecoilState } from 'recoil';

import { useBugoHook } from '@shared/api/bugo/bugo.hook';
import { bugoStateSelectorFamily } from '@shared/api/bugo/bugo.selector';
import {
  giftItemForStoreDataAtomFamily,
  giftItemForStoreStatusAtomFamily,
} from '@shared/api/giftItem/giftItem.atom';
import { useGiftItemForStoreHook } from '@shared/api/giftItem/giftItem.hook';
import {
  AdditionalOption,
  GiftItemInfoType,
} from '@shared/api/giftItemInfo/giftItemInfo.interface';
import {
  GiftAddressRegion,
  GiftOrderCreateDto,
  GiftOrderDeliveryMode,
  GiftSelectedOption,
} from '@shared/api/giftOrder/giftOrder.interface';
import { Image } from '@shared/api/media/media.interface';
import { Loading } from '@shared/components/Loading';
import { StoreUserMenuButton } from '@shared/components/StoreUserMenuButton';
import { useAuth } from '@shared/state/hooks/useAuth';
import { TextField } from '@shared/components/TextField';
import { formatPrice } from '@shared/utils/formatPrice';
import { getGiftAgentOrder } from '@shared/utils/giftAgentOrder.util';
import { phoneNumberStringFormator } from '@shared/utils/phoneNumberStringFormator';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { GiftCallHelperBox } from '../components/GiftCallHelperBox';
import { GiftCheckoutItemCard } from '../components/GiftCheckoutItemCard';
import { GiftCheckoutPayBar } from '../components/GiftCheckoutPayBar';
import { GiftDeliveryFeeSummaryRow } from '../components/GiftDeliveryFeeSummaryRow';
import GiftDeliveryModeSelector from '../components/GiftDeliveryModeSelector';
import { GiftPrivacyNotice } from '../components/GiftPrivacyNotice';
import { giftStoreNavBarTitleAtom } from '../containers/GiftStoreNavBar/giftStoreNavBar.atom';
import { GiftThanksPrintSummary } from '../containers/GiftThanksPrintSection/GiftThanksPrintSummary';
import { useGiftCheckoutDeliveryFee } from '../hooks/useGiftCheckoutDeliveryFee';
import { useGiftCheckoutPayment } from '../hooks/useGiftCheckoutPayment';
import { useGiftDeliveryAddressMemory } from '../hooks/useGiftDeliveryAddressMemory';
import { giftThanksPrintForBugoSelector } from '../state/giftThanksPrint.atom';
import { getGiftOrderDtoSingle } from '../utils/getGiftOrderDto';
import { resolveAllowIndividualDelivery } from '../utils/resolveAllowIndividualDelivery';
import {
  pickSurchargeSuggestion,
  resolveFreeShippingSuggestion,
} from '../utils/giftFreeShippingNudge.util';
import { getUnitSurcharge, resolveShippingFeeConfig } from '../utils/resolveDeliveryFee';
import {
  buildThanksPrintDto,
  resolveOrderThanksPrint,
} from '../utils/thanksPrintMessage';

interface CheckoutItem {
  itemId: string;
  itemName: string;
  itemImage: string;
  priceRetail: number;
  quantity: number;
  minOrderQuantity: number;
  orderUnit: number;
}

const GiftDirectCheckoutPage: FC = function GiftDirectCheckoutPage() {
  const navigate = useNavigate();
  const { itemId } = useParams<{ itemId?: string }>();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const bugoId = searchParams.get('bugoId');
  const initialQuantity = parseInt(searchParams.get('quantity') ?? '1', 10);

  // 상세페이지에서 전달된 선택옵션 (바로구매). 결제화면에서도 변경 가능하도록 state로 보관
  const [selectedOptions, setSelectedOptions] = useState<GiftSelectedOption[]>(
    () =>
      (location.state as { selectedOptions?: GiftSelectedOption[] } | null)
        ?.selectedOptions ?? [],
  );
  const optionDelta = useMemo(
    () => selectedOptions.reduce((s, o) => s + (o.priceDelta || 0), 0),
    [selectedOptions],
  );

  const setTitle = useSetRecoilState(giftStoreNavBarTitleAtom);

  // 상품 조회
  useGiftItemForStoreHook(itemId ?? '');
  const itemStatus = useRecoilValue(giftItemForStoreStatusAtomFamily(itemId ?? ''));
  const item = useRecoilValue(giftItemForStoreDataAtomFamily(itemId ?? ''));

  // bugo 데이터 조회
  useBugoHook(bugoId ?? '', !bugoId);
  const { data: bugoData } = useRecoilValue(bugoStateSelectorFamily(bugoId ?? ''));

  // 로그인한 게스트(StoreGuest) 프로필 — 주문자 정보 prefill에 사용
  const { userProfile } = useAuth();

  useEffect(() => {
    setTitle('주문/결제');
  }, [setTitle]);

  // 상품 정보 파생
  const checkoutItem: CheckoutItem | null = useMemo(() => {
    if (!item || !itemId) return null;

    const productName = item.name || item.giftItemInfo?.name || '';
    const mainImage = item.image || item.giftItemInfo?.image;
    const mainImageUrl =
      mainImage && typeof mainImage === 'object' && 'url' in mainImage
        ? (mainImage as Image).url
        : '';
    const priceRetail = item.priceRetail || 0;
    const minOrderQuantity = item.giftItemInfo?.minOrderQuantity || 1;
    const orderUnit = item.giftItemInfo?.orderUnit || 1;

    return {
      itemId,
      itemName: productName,
      itemImage: mainImageUrl,
      priceRetail,
      quantity: initialQuantity,
      minOrderQuantity,
      orderUnit,
    };
  }, [item, itemId, initialQuantity]);

  // 수량 상태
  const [quantity, setQuantity] = useState(initialQuantity);

  // 초기 수량 설정 — 1회만. 창 포커스 refetch로 checkoutItem 참조가 바뀔 때마다
  // 재실행되면 이 화면에서 조정한 수량이 URL 초기값으로 되돌아간다
  const quantitySeededRef = useRef(false);
  useEffect(() => {
    if (!checkoutItem || quantitySeededRef.current) return;
    quantitySeededRef.current = true;
    setQuantity(initialQuantity || checkoutItem.minOrderQuantity);
  }, [checkoutItem, initialQuantity]);

  // 주문자 정보 (member 정보 기반) — 대리주문 모드면 상주 이름·연락처를 미리 채운다
  const [receiverName, setReceiverName] = useState(
    () => getGiftAgentOrder(bugoId ?? undefined)?.memberName ?? '',
  );
  const [receiverTel, setReceiverTel] = useState(() =>
    (getGiftAgentOrder(bugoId ?? undefined)?.memberPhone ?? '').replace(/[^0-9]/g, ''),
  );

  // 미입력 alert 시 스크롤 타겟
  const receiverNameRef = useRef<HTMLInputElement>(null);
  const receiverTelRef = useRef<HTMLInputElement>(null);
  const addressButtonRef = useRef<HTMLButtonElement>(null);

  // 배송 방식 관련 상태
  const [deliveryMode, setDeliveryMode] = useState<GiftOrderDeliveryMode>(
    GiftOrderDeliveryMode.Bulk,
  );
  const [bulkReceiverAddress, setBulkReceiverAddress] = useState('');
  const [bulkReceiverPostalCode, setBulkReceiverPostalCode] = useState('');
  const [bulkReceiverAddressDetail, setBulkReceiverAddressDetail] = useState('');
  // 카카오 응답 원문(시도·읍면동) — 권역 배송비 판정용. 주소 검색 완료 시 함께 갱신
  const [bulkReceiverRegion, setBulkReceiverRegion] = useState<
    GiftAddressRegion | undefined
  >();

  // 배송형 상품 여부
  const isDeliveryItem = useMemo(() => {
    if (!item) return false;
    return item.giftItemInfo?.type !== GiftItemInfoType.Coupon;
  }, [item]);

  // 개별배송 허용 여부 (상품값 → 브랜드값 오버라이드)
  const allowIndividual = useMemo(() => {
    if (!isDeliveryItem) return true;
    return resolveAllowIndividualDelivery(item?.giftItemInfo);
  }, [isDeliveryItem, item]);

  // 개별배송 불가 상품이면 일괄배송(Bulk)으로 강제
  useEffect(() => {
    if (!allowIndividual && deliveryMode === GiftOrderDeliveryMode.Individual) {
      setDeliveryMode(GiftOrderDeliveryMode.Bulk);
    }
  }, [allowIndividual, deliveryMode]);

  // 최근 배송지 기억(localStorage) — 결제 실패/새로고침에도 배송지 보존 + 칩으로 재적용
  const {
    saved: savedAddress,
    applySaved: applySavedAddress,
    showChip: showSavedAddressChip,
    clear: clearSavedAddress,
    canClear: canClearAddress,
  } = useGiftDeliveryAddressMemory({
    address: bulkReceiverAddress,
    postalCode: bulkReceiverPostalCode,
    addressDetail: bulkReceiverAddressDetail,
    region: bulkReceiverRegion,
    setAddress: setBulkReceiverAddress,
    setPostalCode: setBulkReceiverPostalCode,
    setAddressDetail: setBulkReceiverAddressDetail,
    setRegion: setBulkReceiverRegion,
    enabled: isDeliveryItem && deliveryMode === GiftOrderDeliveryMode.Bulk,
    bugoId,
  });

  // 권역 배송비 미리보기 — 일괄배송 + 주소 입력 뒤, 수량/옵션이 바뀔 때마다 서버에 다시 묻는다
  // (DTO 배송비·결제 차단 검증·버튼 라벨까지 장바구니 결제와 공용 훅)
  const isBulkDelivery = isDeliveryItem && deliveryMode === GiftOrderDeliveryMode.Bulk;
  const previewItems = useMemo(
    () =>
      item
        ? [
            {
              giftItem: item._id,
              quantity,
              selectedOptions: selectedOptions.length > 0 ? selectedOptions : undefined,
            },
          ]
        : [],
    [item, quantity, selectedOptions],
  );
  const {
    deliveryFee,
    deliveryFeeDto,
    allowedPaymentMethods,
    validateDeliveryAddress,
    payButtonLabel,
  } = useGiftCheckoutDeliveryFee({
    items: previewItems,
    region: bulkReceiverRegion,
    isBulkDelivery,
    receiverAddress: bulkReceiverAddress,
    addressButtonRef,
  });

  // 무료배송까지 "몇 개 더" — 단일 상품이라 후보는 이 상품 하나
  const freeShippingSuggestion = useMemo(() => {
    if (!item || !checkoutItem) return null;
    const brand = item.giftItemInfo?.giftItemBrand;
    return resolveFreeShippingSuggestion(deliveryFee.preview, [
      {
        key: item._id,
        itemName: checkoutItem.itemName,
        unitPrice:
          checkoutItem.priceRetail +
          selectedOptions.reduce((sum, o) => sum + o.priceDelta, 0),
        orderUnit: checkoutItem.orderUnit,
        brandId: typeof brand === 'object' ? brand?._id : brand,
      },
    ]);
  }, [deliveryFee.preview, item, checkoutItem, selectedOptions]);
  // 소량주문 추가금이 붙어 있으면 면제 수량까지 "몇 개 더"
  const surchargeSuggestion = useMemo(() => {
    if (!item || !checkoutItem) return null;
    if (item.giftItemInfo?.type === GiftItemInfoType.Coupon) return null;
    return pickSurchargeSuggestion([
      {
        key: item._id,
        itemName: checkoutItem.itemName,
        unitPrice:
          checkoutItem.priceRetail +
          selectedOptions.reduce((sum, o) => sum + o.priceDelta, 0),
        quantity,
        orderUnit: checkoutItem.orderUnit,
        ...resolveShippingFeeConfig(item.giftItemInfo),
      },
    ]);
  }, [item, checkoutItem, selectedOptions, quantity]);
  const addForFreeShipping = useCallback(
    (suggestion: { addQty: number }) => setQuantity((prev) => prev + suggestion.addQty),
    [],
  );

  // 감사메시지 인쇄 — 해당 브랜드 상품일 때만 (장바구니 결제와 동일한 공용 리졸버 사용). 주문당 1건.
  const thanksPrintInfo = useMemo(
    () =>
      resolveOrderThanksPrint(
        item ? [{ itemId: item._id, giftItemInfo: item.giftItemInfo }] : [],
      ),
    [item],
  );

  // 감사메시지 인쇄 입력값 (작성/수정은 GiftThanksPrintSummary가 atom에 보존)
  //* 이 상주(부고)로 작성된 감사인쇄 입력값만 사용 — 다른 상주 응대 중 남은 값 차단
  const thanksSelection = useRecoilValue(giftThanksPrintForBugoSelector(bugoId));

  // 주문자 정보 초기값 — 로그인한 게스트(StoreGuest) 본인이 실제 주문자이므로 userProfile.info 우선,
  // 없을 때만 bugo 상주(member) 정보로 폴백. 데이터가 처음 도착했을 때 1회만 시딩
  // (창 포커스 refetch 재실행 시 사용자가 고친 입력값을 덮어쓰지 않도록)
  const receiverSeededRef = useRef(false);
  useEffect(() => {
    // 대리주문 모드: 주문자는 상주로 고정(초기값 유지) — 로그인 프로필(관리자)로
    // 덮어쓰면 관리자 이름·번호로 게스트가 만들어지고 입금안내 문자도 관리자에게
    // 가는 사고가 된다
    if (bugoId && getGiftAgentOrder(bugoId)) return;
    if (receiverSeededRef.current) return;
    const name = userProfile?.info?.name || bugoData?.member?.fullName || '';
    const phone = userProfile?.info?.phoneNumber || bugoData?.member?.phoneNumber || '';
    if (!name && !phone) return;
    receiverSeededRef.current = true;
    if (name) setReceiverName(name);
    if (phone) setReceiverTel(phone);
  }, [userProfile, bugoData, bugoId]);

  // GiftOrderCreateDto 생성 함수
  const createOrderDto = useCallback((): GiftOrderCreateDto | null => {
    if (!item || !bugoData || !checkoutItem) {
      return null;
    }

    const isBulk = isDeliveryItem && deliveryMode === GiftOrderDeliveryMode.Bulk;

    return getGiftOrderDtoSingle({
      giftItem: item,
      bugo: bugoData,
      quantity,
      selectedOptions: selectedOptions.length > 0 ? selectedOptions : undefined,
      receiverName,
      receiverPhoneNumber: receiverTel,
      deliveryMode: isDeliveryItem ? deliveryMode : undefined,
      bulkDeliveryAddress: isBulk
        ? {
            name: receiverName,
            phoneNumber: receiverTel,
            address: bulkReceiverAddress,
            addressDetail: bulkReceiverAddressDetail || undefined,
            postalCode: bulkReceiverPostalCode,
            ...(bulkReceiverRegion ?? {}),
          }
        : undefined,
      // 권역 배송비 — 미리보기 total 그대로 (권역 브랜드면 0원이어도 필수)
      deliveryFee: isBulk ? deliveryFeeDto : undefined,
      // 감사메시지 인쇄 — 인쇄 대상 상품 + 작성된 내용이 있을 때만 (주문당 1건)
      thanksPrint: thanksPrintInfo
        ? buildThanksPrintDto(
            thanksSelection,
            thanksPrintInfo.config,
            thanksPrintInfo.brandId,
            thanksPrintInfo.allowedTemplateIds,
          )
        : undefined,
    });
  }, [
    item,
    bugoData,
    checkoutItem,
    quantity,
    selectedOptions,
    receiverName,
    receiverTel,
    isDeliveryItem,
    deliveryMode,
    bulkReceiverAddress,
    bulkReceiverAddressDetail,
    bulkReceiverPostalCode,
    bulkReceiverRegion,
    deliveryFeeDto,
    thanksPrintInfo,
    thanksSelection,
  ]);

  // 결제 훅 사용 (배송지 검증은 useGiftCheckoutDeliveryFee가 만든 validateDeliveryAddress)
  const { handlePayment, isProcessing } = useGiftCheckoutPayment({
    createOrderDto,
    bugoId,
    receiverName,
    receiverTel,
    hasItems: !!checkoutItem,
    validateDeliveryAddress,
    receiverNameRef,
    receiverTelRef,
    allowedPaymentMethods,
  });

  // 수량 증가
  const increaseQuantity = useCallback(() => {
    if (!checkoutItem) return;
    setQuantity((prev) => prev + checkoutItem.orderUnit);
  }, [checkoutItem]);

  // 수량 감소
  const decreaseQuantity = useCallback(() => {
    if (!checkoutItem) return;
    setQuantity((prev) => {
      const newQty = prev - checkoutItem.orderUnit;
      return newQty >= checkoutItem.minOrderQuantity ? newQty : prev;
    });
  }, [checkoutItem]);

  // 개당 소량주문 추가금 (수량이 면제 기준 이상이면 0). 상품값 → 브랜드값 오버라이드
  const unitSurcharge = useMemo(() => {
    if (!item || !isDeliveryItem) return 0;
    return getUnitSurcharge(item.giftItemInfo, quantity);
  }, [item, isDeliveryItem, quantity]);

  // 상품 금액 (옵션 + 소량주문 추가금까지 단가에 포함)
  const goodsTotal = useMemo(() => {
    if (!checkoutItem) return 0;
    return (checkoutItem.priceRetail + optionDelta + unitSurcharge) * quantity;
  }, [checkoutItem, quantity, optionDelta, unitSurcharge]);

  // 추가금 면제까지 더 담으면 되는 수량 — 구매 유도용
  const surchargeNudge = useMemo(() => {
    if (!item || !isDeliveryItem) return null;
    const { shippingFee, freeShippingMinQuantity } = resolveShippingFeeConfig(
      item.giftItemInfo,
    );
    if (
      !shippingFee ||
      shippingFee <= 0 ||
      freeShippingMinQuantity == null ||
      quantity >= freeShippingMinQuantity
    ) {
      return null;
    }
    return {
      remain: freeShippingMinQuantity - quantity,
      minQty: freeShippingMinQuantity,
      unitSurcharge: shippingFee,
    };
  }, [item, isDeliveryItem, quantity]);

  // 총 결제 금액 = 상품 금액 (추가금 포함) + 권역 배송비 (DTO의 calcGiftOrderPayment와 같은 식)
  const totalPrice = goodsTotal + deliveryFee.feeTotal;

  // 로딩 중
  if (itemStatus !== 'success' || !item || !checkoutItem) {
    return (
      <div className="center-box min-h-screen">
        <Loading />
      </div>
    );
  }

  // bugoId가 없으면 리다이렉트
  if (!bugoId) {
    navigate('/gift-store');
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <StoreUserMenuButton floatingTopClassName="top-16" />
      {/* 1. 주문자 정보 */}
      <div className="bg-white p-4">
        <h3 className="mb-3 font-bold">주문자 정보</h3>
        <div className="space-y-3">
          <TextField
            ref={receiverNameRef}
            scrollOnFocus={true}
            className="w-full"
            placeholder="주문자 성함"
            value={receiverName}
            onChange={(e) => setReceiverName(e.target.value)}
          />
          <TextField
            ref={receiverTelRef}
            scrollOnFocus={true}
            type="tel"
            className="w-full"
            placeholder="휴대전화번호"
            value={phoneNumberStringFormator(receiverTel)}
            maxLength={13}
            onChange={(e) => setReceiverTel(e.target.value.replace(/[^0-9]/gi, ''))}
          />
        </div>
      </div>

      {/* 2. 구매 상품 */}
      <div className="mt-2 bg-white p-4">
        <h3 className="mb-3 font-bold">구매 상품</h3>
        <div className="space-y-3">
          <GiftCheckoutItemCard
            itemName={checkoutItem.itemName}
            itemImage={checkoutItem.itemImage}
            priceRetail={checkoutItem.priceRetail}
            quantity={quantity}
            minOrderQuantity={checkoutItem.minOrderQuantity}
            orderUnit={checkoutItem.orderUnit}
            selectedOptions={selectedOptions}
            additionalOptions={
              item.giftItemInfo?.additionalOptions as
                | AdditionalOption<Image>[]
                | undefined
            }
            onChangeOptions={setSelectedOptions}
            onIncrease={increaseQuantity}
            onDecrease={decreaseQuantity}
            itemType={item.giftItemInfo?.type}
            deliveryMode={deliveryMode}
            unitSurcharge={unitSurcharge}
            surchargeMinQty={
              resolveShippingFeeConfig(item.giftItemInfo).freeShippingMinQuantity
            }
          />
        </div>

        {/* 대량구매 혜택 유도 */}
        {surchargeNudge && (
          <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            <span className="font-bold">{surchargeNudge.remain}개</span>만 더 담으면 개당{' '}
            <span className="font-bold">
              {formatPrice(surchargeNudge.unitSurcharge)}원
            </span>{' '}
            더 저렴해져요! (
            <span className="font-bold">{surchargeNudge.minQty}개 이상</span>)
          </div>
        )}
      </div>

      {/* 2-1. 감사 메시지 인쇄 (감사 인쇄 가능 상품일 때) */}
      {thanksPrintInfo && (
        <div className="mt-2">
          <GiftThanksPrintSummary
            thanksPrint={thanksPrintInfo.config}
            brandId={thanksPrintInfo.brandId}
            allowedTemplateIds={thanksPrintInfo.allowedTemplateIds}
            bugoId={bugoId}
            eligibleCount={thanksPrintInfo.eligibleCount}
          />
        </div>
      )}

      {/* 3. 배송 방식 선택 (배송형 상품일 때만) */}
      {isDeliveryItem && (
        <div className="mt-2">
          <GiftDeliveryModeSelector
            deliveryMode={deliveryMode}
            onDeliveryModeChange={setDeliveryMode}
            receiverAddress={bulkReceiverAddress}
            onReceiverAddressChange={setBulkReceiverAddress}
            receiverPostalCode={bulkReceiverPostalCode}
            onReceiverPostalCodeChange={setBulkReceiverPostalCode}
            receiverAddressDetail={bulkReceiverAddressDetail}
            onReceiverAddressDetailChange={setBulkReceiverAddressDetail}
            onReceiverRegionChange={setBulkReceiverRegion}
            deliveryFee={deliveryFee}
            freeShippingSuggestion={freeShippingSuggestion}
            onAddForFreeShipping={addForFreeShipping}
            allowIndividual={allowIndividual}
            addressButtonRef={addressButtonRef}
            savedAddress={savedAddress}
            showSavedChip={showSavedAddressChip}
            onApplySaved={applySavedAddress}
            showClear={canClearAddress}
            onClear={clearSavedAddress}
          />
        </div>
      )}

      {/* 4. 최종 결제 금액 */}
      <div className="mt-2 bg-white p-4">
        <h3 className="mb-3 font-bold">결제 금액</h3>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-500">상품 금액</span>
            <span>{formatPrice(goodsTotal)}원</span>
          </div>
          <GiftDeliveryFeeSummaryRow deliveryFee={deliveryFee} />
          <div className="flex justify-between border-t pt-2">
            <span className="font-bold">총 결제 금액</span>
            <span className="text-lg font-bold text-red-500">
              {formatPrice(totalPrice)}원
            </span>
          </div>
        </div>
      </div>

      {/* 5. 개인정보 동의 (체크 없이 표시만) */}
      <div className="mt-2 bg-white p-4">
        <GiftPrivacyNotice />
      </div>

      {/* 6. 결제 도움 안내 */}
      <div className="mt-2 bg-white">
        <GiftCallHelperBox />
      </div>

      {/* 7. 하단 고정 결제 버튼 */}
      <GiftCheckoutPayBar
        label={payButtonLabel(totalPrice, isProcessing)}
        disabled={isProcessing || deliveryFee.blocksPayment}
        onPay={handlePayment}
        deliveryFee={deliveryFee}
        freeShippingSuggestion={freeShippingSuggestion}
        onAddForFreeShipping={addForFreeShipping}
        surchargeSuggestion={surchargeSuggestion}
        onAddForSurcharge={addForFreeShipping}
      />
    </div>
  );
};

export default GiftDirectCheckoutPage;
